import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Stack, Avatar, alpha, CircularProgress, Alert, Button, Chip, Divider,
  TextField, InputAdornment, Grid,
} from '@mui/material';
import { Search, Wifi, PersonAdd, WifiOff, Star, Place, Phone, Close, Schedule, Payments, DirectionsCar } from '@mui/icons-material';
import {
  getAdminUsers, assignRideDriver, assignPorterPartner, assignOnDemandProvider,
  updateRideStatus, updateOnDemandStatus,
} from '../../services/businessService';
import { autoAssignJob, ENTITY_TYPE_MAP, getDriverMonitoring } from '../../services/dispatcherService';
import { listAvailableDrivers } from '../../services/serviceHubService';
import { DispatchRow, SERVICE_META, isLiveTrip, formatStatus, timeAgo } from './dispatchTypes';
import DispatchStatusActions from './DispatchStatusActions';
import DispatchTripProgress from './DispatchTripProgress';
import { DISPATCH, dispatchScroll, panelShell } from './dispatchTheme';
import { formatMoney } from '../../utils/currency';

interface DispatchDriverPanelProps {
  selected: DispatchRow | null;
  onAssigned: () => void;
  onBook: () => void;
  bookCtaLabel?: string;
  onClear?: () => void;
}

const ASSIGN_TYPES = new Set(['transportation', 'porter', 'on_demand']);

type PartnerCard = {
  _id: string;
  name: string;
  phone?: string;
  email?: string;
  rating?: number;
  vehicleNumber?: string;
  selectable: boolean;
};

export default function DispatchDriverPanel({ selected, onAssigned, onBook, bookCtaLabel = 'Create New Booking', onClear }: DispatchDriverPanelProps) {
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [driverSearch, setDriverSearch] = useState('');

  useEffect(() => {
    setError('');
    setStatusError('');
    setDriverSearch('');
  }, [selected?._id, selected?.service]);

  const partnerType = selected?.service === 'transportation' ? 'driver'
    : selected?.service === 'on_demand' ? 'service_provider'
      : selected?.service === 'porter' ? 'delivery_partner'
        : null;

  const isTransport = selected?.service === 'transportation';

  const { data: onlineDrivers = [], isLoading: driversLoading } = useQuery({
    queryKey: ['available-drivers'],
    queryFn: listAvailableDrivers,
    enabled: isTransport && !!selected,
    refetchInterval: 10000,
  });

  const { data: roster = [], isLoading: rosterLoading } = useQuery({
    queryKey: ['dispatch-roster', partnerType],
    queryFn: () => getAdminUsers({ userType: partnerType! }),
    enabled: !!partnerType && !!selected && !isTransport,
  });

  const { data: driverMonitor } = useQuery({
    queryKey: ['driver-monitor', selected?.partnerId],
    queryFn: () => getDriverMonitoring(selected!.partnerId!),
    enabled: isTransport && !!selected?.partnerId && isLiveTrip(selected),
    refetchInterval: 8000,
  });

  const partners: PartnerCard[] = useMemo(() => {
    if (isTransport) {
      return onlineDrivers.map((d) => ({
        _id: String(d.id),
        name: d.name || 'Driver',
        phone: d.phone,
        rating: d.rating,
        vehicleNumber: d.vehicleNumber,
        selectable: d.isOnline && d.isAvailable,
      }));
    }
    return roster.map((p: { _id: string; firstName?: string; lastName?: string; phone?: string; email?: string }) => ({
      _id: p._id,
      name: [p.firstName, p.lastName].filter(Boolean).join(' ') || 'Partner',
      phone: p.phone,
      email: p.email,
      selectable: true,
    }));
  }, [isTransport, onlineDrivers, roster]);

  const filteredPartners = useMemo(() => {
    const q = driverSearch.trim().toLowerCase();
    if (!q) return partners;
    return partners.filter((p) =>
      p.name.toLowerCase().includes(q)
      || (p.phone || '').includes(q)
      || (p.email || '').toLowerCase().includes(q)
      || (p.vehicleNumber || '').toLowerCase().includes(q),
    );
  }, [partners, driverSearch]);

  const isLoading = isTransport ? driversLoading : rosterLoading;

  const canAssign = selected
    && ASSIGN_TYPES.has(selected.service)
    && !['completed', 'cancelled', 'delivered'].includes(selected.status);

  const handleAssign = async (partnerId: string) => {
    if (!selected) return;
    setAssigningId(partnerId);
    setError('');
    try {
      if (selected.service === 'transportation') await assignRideDriver(selected._id, partnerId);
      else if (selected.service === 'porter') await assignPorterPartner(selected._id, partnerId);
      else if (selected.service === 'on_demand') await assignOnDemandProvider(selected._id, partnerId);
      onAssigned();
    } catch (e: unknown) {
      setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Assign failed');
    } finally {
      setAssigningId(null);
    }
  };

  const canAutoAssign = selected && ['transportation', 'porter', 'on_demand'].includes(selected.service)
    && !['completed', 'cancelled', 'delivered'].includes(selected.status);

  const handleAutoAssign = async () => {
    if (!selected) return;
    setAutoAssigning(true);
    setError('');
    try {
      await autoAssignJob({
        serviceType: selected.service,
        entityType: ENTITY_TYPE_MAP[selected.service] || 'ride',
        entityId: selected._id,
        coordinates: selected.pickupLng && selected.pickupLat
          ? [selected.pickupLng, selected.pickupLat]
          : undefined,
      });
      onAssigned();
    } catch (e: unknown) {
      setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Auto-assign failed');
    } finally {
      setAutoAssigning(false);
    }
  };

  const entityMap: Record<string, string> = {
    transportation: 'transport',
    porter: 'porter_order',
    on_demand: 'on_demand_booking',
  };

  const onlineCount = partners.filter((p) => p.selectable).length;
  const meta = selected ? SERVICE_META[selected.service] : null;
  const unassigned = selected && (selected.partner === 'Unassigned' || selected.partner === '-');

  if (!selected) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: DISPATCH.bgMuted }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${DISPATCH.border}`, bgcolor: DISPATCH.bgPanel }}>
          <Typography variant="subtitle1" fontWeight={800}>Job Details</Typography>
          <Typography variant="caption" color="text.secondary">Select a job from the queue</Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3, textAlign: 'center' }}>
          <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: alpha(DISPATCH.primary, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <PersonAdd sx={{ fontSize: 32, color: DISPATCH.primary }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={800} mb={0.5}>No job selected</Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5} maxWidth={280}>
            Tap a job in the queue to view details, update status, or assign a partner.
          </Typography>
          <Button variant="contained" startIcon={<PersonAdd />} onClick={onBook}
            sx={{ fontWeight: 800, textTransform: 'none', borderRadius: 2.5, bgcolor: DISPATCH.primary, px: 3 }}>
            {bookCtaLabel}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: DISPATCH.bgMuted, minHeight: 0 }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${DISPATCH.border}`, bgcolor: DISPATCH.bgPanel, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.25}>
            <Typography variant="subtitle1" fontWeight={800}>#{selected.ref}</Typography>
            {meta && (
              <Chip label={meta.label} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(meta.color, 0.1), color: meta.color }} />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {isTransport ? 'Assign driver or update trip status' : 'Update status or assign partner'}
          </Typography>
        </Box>
        {onClear && (
          <Button size="small" onClick={onClear} sx={{ minWidth: 'auto', p: 0.5 }}>
            <Close fontSize="small" />
          </Button>
        )}
      </Box>

      <Box sx={{ ...dispatchScroll, p: 2 }}>
        {(isLiveTrip(selected) || !['completed', 'cancelled', 'delivered', 'refunded'].includes(selected.status)) && (
          <Box sx={{ ...panelShell(), p: 1.5, mb: 2 }}>
            <DispatchTripProgress row={selected} compact />
          </Box>
        )}

        <Box sx={{ ...panelShell(), p: 1.5, mb: 2 }}>
          <Typography variant="body1" fontWeight={700} mb={0.25}>{selected.customer}</Typography>
          {selected.customerPhone && (
            <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
              <Phone sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary">{selected.customerPhone}</Typography>
            </Stack>
          )}

          <Grid container spacing={1} mb={1} sx={{ minWidth: 0, width: '100%', m: 0 }}>
            <Grid item xs={6} sx={{ minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">Status</Typography>
              <Chip label={formatStatus(selected.status)} size="small" sx={{ mt: 0.25, fontWeight: 700, height: 22 }} />
            </Grid>
            <Grid item xs={6} sx={{ minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">Created</Typography>
              <Typography variant="body2" fontWeight={600}>{timeAgo(selected.createdAt)}</Typography>
            </Grid>
            {selected.scheduledAt && (
              <Grid item xs={6}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Schedule sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">Scheduled</Typography>
                    <Typography variant="caption">{new Date(selected.scheduledAt).toLocaleString()}</Typography>
                  </Box>
                </Stack>
              </Grid>
            )}
            {selected.vehicleType && (
              <Grid item xs={6}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <DirectionsCar sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">Vehicle</Typography>
                    <Typography variant="caption">{selected.vehicleType}</Typography>
                  </Box>
                </Stack>
              </Grid>
            )}
            {selected.paymentMethod && (
              <Grid item xs={6}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Payments sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">Payment</Typography>
                    <Typography variant="caption">{selected.paymentMethod}</Typography>
                  </Box>
                </Stack>
              </Grid>
            )}
          </Grid>

          {selected.pickupAddress && (
            <Stack direction="row" alignItems="flex-start" spacing={0.75} mb={0.5}>
              <Place sx={{ fontSize: 14, color: '#22c55e', mt: 0.2 }} />
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">PICKUP</Typography>
                <Typography variant="body2">{selected.pickupAddress}</Typography>
              </Box>
            </Stack>
          )}
          {selected.dropoffAddress && (
            <Stack direction="row" alignItems="flex-start" spacing={0.75} mb={1}>
              <Place sx={{ fontSize: 14, color: '#ef4444', mt: 0.2 }} />
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">DROP</Typography>
                <Typography variant="body2">{selected.dropoffAddress}</Typography>
              </Box>
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between" alignItems="center" pt={1} borderTop={1} borderColor="divider">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Partner</Typography>
              <Typography variant="body2" fontWeight={700} color={unassigned ? 'error.main' : 'text.primary'}>
                {selected.partner}
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="caption" color="text.secondary" display="block">Fare</Typography>
              <Typography variant="h6" fontWeight={800} color="primary.main">
                {selected.total > 0 ? `${formatMoney(selected.total)}` : '—'}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {driverMonitor && (
          <Box sx={{ ...panelShell(), p: 1.5, mb: 2, borderColor: alpha(DISPATCH.success, 0.35) }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="overline" color="success.main" fontWeight={800} letterSpacing="0.06em">
                Live Driver Tracking
              </Typography>
              <Chip
                label={driverMonitor.isOnline ? (driverMonitor.isAvailable ? 'Online' : 'On trip') : 'Offline'}
                size="small"
                color={driverMonitor.isOnline ? 'success' : 'default'}
                sx={{ height: 22, fontWeight: 700, fontSize: '0.65rem' }}
              />
            </Stack>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {driverMonitor.vehicleNumber && (
                <Typography variant="caption"><strong>Vehicle:</strong> {driverMonitor.vehicleNumber}</Typography>
              )}
              {driverMonitor.speed != null && (
                <Typography variant="caption"><strong>Speed:</strong> {Math.round(driverMonitor.speed)} km/h</Typography>
              )}
              {driverMonitor.rating != null && (
                <Typography variant="caption"><strong>Rating:</strong> {driverMonitor.rating.toFixed(1)}</Typography>
              )}
              {driverMonitor.batteryLevel != null && (
                <Typography variant="caption"><strong>Battery:</strong> {driverMonitor.batteryLevel}%</Typography>
              )}
            </Stack>
          </Box>
        )}

        <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing="0.06em" display="block" mb={1}>
          Update Status
        </Typography>
        {canAutoAssign && (
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            disabled={autoAssigning}
            onClick={handleAutoAssign}
            sx={{ mb: 1.5, fontWeight: 700, borderRadius: 2 }}
          >
            {autoAssigning ? 'Auto-assigning…' : 'Smart Auto-Assign'}
          </Button>
        )}
        <Box sx={{ ...panelShell(), p: 1.5, mb: 2 }}>
          {statusError && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setStatusError('')}>{statusError}</Alert>}
          <DispatchStatusActions
            entityType={entityMap[selected.service] || 'ride'}
            entityId={selected._id}
            currentStatus={selected.status}
            onStatusChange={async (status) => {
              setStatusError('');
              try {
                if (selected.service === 'transportation') await updateRideStatus(selected._id, status);
                else if (selected.service === 'on_demand') await updateOnDemandStatus(selected._id, status);
                else throw new Error('Status updates are not supported for this service');
                onAssigned();
              } catch (e: unknown) {
                setStatusError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Status update failed');
              }
            }}
          />
        </Box>

        {canAssign && partnerType && (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing="0.06em">
                {isTransport ? 'Assign Driver' : 'Assign Partner'}
              </Typography>
              <Chip
                label={isTransport ? `${onlineCount} online` : `${partners.length} available`}
                size="small"
                color={onlineCount > 0 || partners.length > 0 ? 'success' : 'warning'}
                variant="outlined"
                sx={{ fontWeight: 700, height: 22, fontSize: '0.65rem' }}
              />
            </Stack>

            <TextField
              fullWidth
              size="small"
              placeholder="Search drivers…"
              value={driverSearch}
              onChange={(e) => setDriverSearch(e.target.value)}
              sx={{ mb: 1.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {isLoading && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            )}
            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

            {!isLoading && filteredPartners.length === 0 && (
              <Alert severity="warning" variant="outlined">
                {isTransport
                  ? 'No drivers online. Drivers must go online in the driver app.'
                  : 'No partners found. Add partners from the Partners page.'}
              </Alert>
            )}

            <Stack spacing={0.75}>
              {filteredPartners.map((p) => (
                <Box
                  key={p._id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 1.25,
                    borderRadius: `${DISPATCH.radiusSm}px`,
                    bgcolor: DISPATCH.bgPanel,
                    border: '1px solid',
                    borderColor: assigningId === p._id ? DISPATCH.success : DISPATCH.border,
                    opacity: !p.selectable ? 0.55 : assigningId && assigningId !== p._id ? 0.5 : 1,
                  }}
                >
                  <Avatar sx={{ width: 38, height: 38, bgcolor: p.selectable ? 'primary.main' : 'grey.400', fontSize: '0.85rem' }}>
                    {p.name[0] || 'D'}
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={700} noWrap>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {p.vehicleNumber || p.phone || p.email || '—'}
                    </Typography>
                  </Box>
                  <Stack alignItems="flex-end" spacing={0.25} sx={{ mr: 0.5 }}>
                    {p.selectable ? <Wifi sx={{ fontSize: 14, color: 'success.main' }} /> : <WifiOff sx={{ fontSize: 14, color: 'text.disabled' }} />}
                    {p.rating != null && (
                      <Stack direction="row" alignItems="center" spacing={0.25}>
                        <Star sx={{ fontSize: 11, color: '#f59e0b' }} />
                        <Typography variant="caption">{p.rating.toFixed(1)}</Typography>
                      </Stack>
                    )}
                  </Stack>
                  <Button
                    size="small"
                    variant={p.selectable ? 'contained' : 'outlined'}
                    disabled={!p.selectable || !!assigningId}
                    onClick={() => handleAssign(p._id)}
                    sx={{ fontWeight: 700, minWidth: 72, borderRadius: 1.5 }}
                  >
                    {assigningId === p._id ? <CircularProgress size={16} color="inherit" /> : 'Assign'}
                  </Button>
                </Box>
              ))}
            </Stack>
          </>
        )}

      </Box>
    </Box>
  );
}
